import {Api, HTTPMethod, buildExport, Endpoint} from "@next/apiconfig"
import {TApiUrlSettings} from "@next/utils"
import env from "../../env"
import {AGGREGATORQUERYTYPES, BUNDLE_PRICE_LABEL_TEXT, SummaryType} from "../../constants"

export interface ProductApiConfig {
    method: "get" | "post" | "put" | "delete"
    aggregatorUrl: (
        apiUrlSettings: TApiUrlSettings,
    ) => (version: string, paramType: string, itemNumber: string) => string
    url: (apiUrlSettings: TApiUrlSettings) => (itemNumber?: string, type?: string) => string
    routeDefinition: string
    localEndpoint: (
        apiProxyVersion?: string,
        itemNumber?: string | number,
        type?: string,
        bundlePriceLabel?: string,
    ) => string
}

const {REACT_APP_API_BASEURL_PRODUCT_SUMMARY, REACT_APP_SERVE_PATH_PREFIX} = env

export class SearchApi implements Api {
    constructor(public baseURL: string = REACT_APP_API_BASEURL_PRODUCT_SUMMARY) {
        this.baseURL = baseURL
    }

    version = "v1"
    endpoints = {
        getProductSummary: {
            routeDefinition: `${REACT_APP_SERVE_PATH_PREFIX}/products/:itemNumber`,
            getLocalURL: (itemNumber: string) => `${REACT_APP_SERVE_PATH_PREFIX}/products/${itemNumber}`,
            getRemoteURL: (itemNumber: string) => `/products/${itemNumber}`,
            method: HTTPMethod.get,
        },
        // initial serverside call/infinite scroll/fetch more colourways
        getProductSummaryJson: {
            routeDefinition: `${REACT_APP_SERVE_PATH_PREFIX}/plp-ui/api/v1/product-summary/:type/:itemNumber`,
            getLocalURL: (
                apiProxyVersion: string,
                itemNumber: string,
                type: SummaryType,
                bundlePriceLabel?: string,
            ) => {
                const params = new URLSearchParams()
                if (bundlePriceLabel) params.append(BUNDLE_PRICE_LABEL_TEXT, bundlePriceLabel)
                const query = params.toString()
                return `${REACT_APP_SERVE_PATH_PREFIX}/plp-ui/api/${apiProxyVersion}/product-summary/${type}/${itemNumber}${
                    query ? `?${query}` : ""
                }`
            },

            getRemoteURL: (itemNumber: string, type: SummaryType) => `/${type}s/${itemNumber}`,
            method: HTTPMethod.get,
        },
        // on click to get more colourways
        getColourwaySummary: {
            routeDefinition: `${REACT_APP_SERVE_PATH_PREFIX}/plp-ui/api/v1/colourways/:type/:itemNumber`,
            getLocalURL: (
                apiProxyVersion: string,
                itemNumber: string,
                type: SummaryType,
                bundlePriceLabel?: string,
            ) => {
                const params = new URLSearchParams()
                if (bundlePriceLabel) params.append(BUNDLE_PRICE_LABEL_TEXT, bundlePriceLabel)
                const query = params.toString()
                return `${REACT_APP_SERVE_PATH_PREFIX}/plp-ui/api/${apiProxyVersion}/colourways/${type}/${itemNumber}${
                    query ? `?${query}` : ""
                }`
            },
            getRemoteURL: (itemNumber: string, type: SummaryType) => `/${type}s/${itemNumber}`,
            method: HTTPMethod.get,
        },
        getProductAggregatorJson: {
            routeDefinition: `${REACT_APP_SERVE_PATH_PREFIX}/plp-ui/api/v1/product-summary/:type/:itemNumber`,
            getLocalURL: (
                apiProxyVersion: string,
                itemNumber: string,
                type: SummaryType,
                bundlePriceLabel?: string,
            ) => {
                const params = new URLSearchParams()
                if (bundlePriceLabel) params.append(BUNDLE_PRICE_LABEL_TEXT, bundlePriceLabel)
                const query = params.toString()
                return `${REACT_APP_SERVE_PATH_PREFIX}/plp-ui/api/${apiProxyVersion}/product-summary/${type}/${itemNumber}${
                    query ? `?${query}` : ""
                }`
            },
            getRemoteURL: (version: string, type: SummaryType) => `${AGGREGATORQUERYTYPES(version)[type]}`,
            method: HTTPMethod.get,
        },
    }
}

const searchApi = (baseUrl?: string) => new SearchApi(baseUrl)
export default (endpoint: Endpoint, baseUrl?: string) => buildExport(searchApi(baseUrl), endpoint) as ProductApiConfig
